import React, { useState, useEffect } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote, Send, X, Loader2, Package, Coffee, Wine, Search, Smartphone } from 'lucide-react';
import api from '../../api/axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';

const PAYMENT_METHODS = [
  { id: 'efectivo', label: 'Efectivo', icon: Banknote },
  { id: 'transferencia', label: 'Transferencia', icon: Send },
  { id: 'tarjeta', label: 'Tarjeta', icon: CreditCard },
  { id: 'mercadopago', label: 'MercadoPago', icon: Smartphone },
];

export const SalesManager = () => {
  const [inventory, setInventory] = useState([]);
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [customerName, setCustomerName] = useState('');
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await api.get('/admin/inventory');
      setInventory(response.data.items.filter(i => i.quantity > 0));
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      if (existing.quantity >= item.quantity) {
        toast.error('No hay más stock disponible');
        return;
      }
      setCart(cart.map(c => 
        c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
      ));
    } else {
      setCart([...cart, { 
        ...item, 
        quantity: 1,
        inventory_id: item.id,
        unit_price: item.price || 0,
        total: item.price || 0
      }]);
    }
  };

  const updateCartQuantity = (itemId, delta) => {
    const item = cart.find(c => c.id === itemId);
    const invItem = inventory.find(i => i.id === itemId);
    
    if (!item) return;
    
    const newQuantity = item.quantity + delta;
    
    if (newQuantity <= 0) {
      setCart(cart.filter(c => c.id !== itemId));
    } else if (newQuantity > (invItem?.quantity || 0)) {
      toast.error('No hay más stock disponible');
    } else {
      setCart(cart.map(c => 
        c.id === itemId ? { 
          ...c, 
          quantity: newQuantity,
          total: newQuantity * c.unit_price
        } : c
      ));
    }
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.id !== itemId));
  };

  const getSubtotal = () => cart.reduce((sum, item) => sum + item.total, 0);
  const getTotal = () => Math.max(0, getSubtotal() - discount);

  const handleSale = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // Si es MercadoPago, crear preferencia y redirigir
    if (paymentMethod === 'mercadopago') {
      setLoading(true);
      try {
        const response = await api.post('/payments/create-preference', {
          type: 'products',
          items: cart.map(item => ({
            name: item.name,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total: item.total,
            inventory_id: item.inventory_id
          }))
        });
        
        if (response.data.init_point) {
          window.location.href = response.data.init_point;
        } else {
          toast.error('Error al iniciar pago con MercadoPago');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al procesar pago');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Pago normal (efectivo, transferencia, tarjeta)
    setLoading(true);
    try {
      const saleData = {
        sale_type: 'product',
        items: cart.map(item => ({
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
          inventory_id: item.inventory_id
        })),
        discount,
        payment_method: paymentMethod,
        customer_name: customerName || null
      };

      await api.post('/admin/sales', saleData);
      toast.success(`Venta registrada: $${getTotal().toLocaleString('es-AR')}`);
      
      // Reset
      setCart([]);
      setDiscount(0);
      setCustomerName('');
      setShowConfirmModal(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar venta');
    } finally {
      setLoading(false);
    }
  };

  const filteredInventory = inventory.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="sales-manager">
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Products Panel */}
        <div className="lg:col-span-2">
          <Card className="glass-effect border-white/10">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <CardTitle className="text-lg">Productos</CardTitle>
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-48 pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredInventory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No hay productos disponibles</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                  {filteredInventory.map(item => {
                    const inCart = cart.find(c => c.id === item.id);
                    const isLowStock = item.quantity <= 3;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => addToCart(item)}
                        className={`p-3 sm:p-4 rounded-xl border-2 text-left transition-all ${
                          inCart 
                            ? 'border-primary bg-primary/10' 
                            : 'border-white/10 hover:border-white/30 bg-white/5'
                        }`}
                        data-testid={`product-${item.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {item.category === 'cafe' ? (
                            <Coffee className="h-5 w-5 text-amber-400" />
                          ) : (
                            <Wine className="h-5 w-5 text-blue-400" />
                          )}
                          {inCart && (
                            <span className="ml-auto bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                              {inCart.quantity}
                            </span>
                          )}
                        </div>
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-primary font-bold">
                            ${item.price?.toLocaleString('es-AR') || '0'}
                          </p>
                          <p className={`text-xs ${isLowStock ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                            Stock: {item.quantity}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cart Panel */}
        <div>
          <Card className="glass-effect border-white/10 sticky top-24">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Carrito
                {cart.length > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full ml-auto">
                    {cart.reduce((sum, i) => sum + i.quantity, 0)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Carrito vacío</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {cart.map(item => (
                      <div key={item.id} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            ${item.unit_price.toLocaleString('es-AR')} c/u
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateCartQuantity(item.id, -1)}
                            className="p-1 hover:bg-white/10 rounded"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-6 text-center text-sm font-bold">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.id, 1)}
                            className="p-1 hover:bg-white/10 rounded"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="p-1 hover:bg-red-500/20 rounded text-red-400 ml-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Customer Name */}
                  <input
                    type="text"
                    placeholder="Nombre del cliente (opcional)"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />

                  {/* Discount */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Descuento:</span>
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-24 px-2 py-1 bg-white/5 border border-white/10 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  {/* Totals */}
                  <div className="border-t border-white/10 pt-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal:</span>
                      <span>${getSubtotal().toLocaleString('es-AR')}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-green-400">
                        <span>Descuento:</span>
                        <span>-${discount.toLocaleString('es-AR')}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-primary">${getTotal().toLocaleString('es-AR')}</span>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Método de pago:</p>
                    <div className="grid grid-cols-3 gap-2">
                      {PAYMENT_METHODS.map(method => (
                        <button
                          key={method.id}
                          onClick={() => setPaymentMethod(method.id)}
                          className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                            paymentMethod === method.id
                              ? 'border-primary bg-primary/20'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <method.icon className="h-4 w-4" />
                          <span className="text-xs">{method.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <Button
                    onClick={() => setShowConfirmModal(true)}
                    className="w-full bg-primary hover:bg-primary/90 h-12 text-lg font-bold"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>Cobrar ${getTotal().toLocaleString('es-AR')}</>
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowConfirmModal(false)}>
          <div 
            className="bg-background border border-white/10 rounded-xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Confirmar Venta</h3>
            
            <div className="space-y-2 mb-4 max-h-[200px] overflow-y-auto">
              {cart.map(item => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span>{item.quantity}x {item.name}</span>
                  <span>${item.total.toLocaleString('es-AR')}</span>
                </div>
              ))}
            </div>
            
            <div className="border-t border-white/10 pt-3 mb-4">
              {discount > 0 && (
                <div className="flex justify-between text-sm text-green-400">
                  <span>Descuento:</span>
                  <span>-${discount.toLocaleString('es-AR')}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold">
                <span>Total:</span>
                <span className="text-primary">${getTotal().toLocaleString('es-AR')}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Pago: {PAYMENT_METHODS.find(m => m.id === paymentMethod)?.label}
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="ghost" 
                onClick={() => setShowConfirmModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSale}
                disabled={loading}
                className="flex-1 bg-primary"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesManager;
